import React from "react";
import { useTranslation } from "react-i18next";
import { LoaderCircle, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import type { SubscriptionPlan } from "@/types/subscription-plan";
import type { TenantSubscription } from "@/types/tenant-subscription";
import { TenantPlanFeatureBadges } from "./TenantPlanFeatureBadges";
import {
  formatDateTime,
  formatMoney,
  getStatusVariant,
  isIncompleteExpiredSubscriptionStatus,
  normalizeSubscriptionPlanFeature,
  normalizeTenantPlanFeature,
} from "./utils";

interface TenantSubscriptionsHistorySectionProps {
  cancelingKey: string | null;
  plans: SubscriptionPlan[];
  subscriptions: TenantSubscription[];
  subscriptionsLoading: boolean;
  onCancel: (subscriptionId: string, cancelAtPeriodEnd: boolean) => void;
}

export const TenantSubscriptionsHistorySection = ({
  cancelingKey,
  plans,
  subscriptions,
  subscriptionsLoading,
  onCancel,
}: TenantSubscriptionsHistorySectionProps): React.JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold text-zinc-950">
          {t("subscriptions.tenantManagement.history.title")}
        </h3>
        {subscriptionsLoading ? (
          <LoaderCircle className="text-muted-foreground h-4 w-4 animate-spin" />
        ) : null}
      </div>

      {subscriptions.length === 0 && !subscriptionsLoading ? (
        <div className="border-border text-muted-foreground rounded-xl border border-dashed p-6 text-sm">
          {t("subscriptions.tenantManagement.history.empty")}
        </div>
      ) : null}

      {subscriptions.map((subscription) => {
        const periodCancelKey = `${subscription.id}:period`;
        const immediateCancelKey = `${subscription.id}:now`;
        const isCancelingPeriod = cancelingKey === periodCancelKey;
        const isCancelingImmediate = cancelingKey === immediateCancelKey;
        const isIncompleteExpired = isIncompleteExpiredSubscriptionStatus(
          subscription.status,
        );
        const canCancel = !["canceled"].includes(
          subscription.status.toLowerCase(),
        );
        const fullPlan = plans.find((plan) => plan.id === subscription.plan.id);
        const normalizedFeatures = fullPlan
          ? fullPlan.features.map(normalizeSubscriptionPlanFeature)
          : subscription.plan.features.map(normalizeTenantPlanFeature);

        return (
          <div
            key={subscription.id}
            className="border-border bg-background rounded-xl border p-4 shadow-xs"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold text-zinc-950">
                    {subscription.plan.title}
                  </h4>
                  <Badge variant={getStatusVariant(subscription.status)}>
                    {subscription.status}
                  </Badge>
                  {!isIncompleteExpired ? (
                    <Badge
                      variant={
                        subscription.plan.enableCoreFeatures
                          ? "new_default"
                          : "new_secondary"
                      }
                    >
                      {subscription.plan.enableCoreFeatures
                        ? t(
                            "subscriptions.tenantManagement.planDetails.coreIncluded",
                          )
                        : t(
                            "subscriptions.tenantManagement.planDetails.coreExcluded",
                          )}
                    </Badge>
                  ) : null}
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                  {formatMoney(
                    subscription.plan.price,
                    subscription.plan.currency,
                  )}
                </p>
                <p className="text-muted-foreground mt-1 text-sm">
                  {t(
                    "subscriptions.tenantManagement.history.assignedHotel",
                    "Assigned hotel",
                  )}
                  {": "}
                  {subscription.hotel?.name ?? "—"}
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="new_outline"
                  size="sm"
                  disabled={!canCancel || isCancelingImmediate}
                  onClick={() => onCancel(subscription.id, true)}
                >
                  {isCancelingPeriod ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : null}
                  {t("subscriptions.tenantManagement.cancel.atPeriodEnd")}
                </Button>
                <Button
                  type="button"
                  variant="new_destructive"
                  size="sm"
                  disabled={!canCancel || isCancelingPeriod}
                  onClick={() => onCancel(subscription.id, false)}
                >
                  {isCancelingImmediate ? (
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  {t("subscriptions.tenantManagement.cancel.now")}
                </Button>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="grid gap-3 text-sm text-zinc-700 sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground text-xs tracking-[0.12em] uppercase">
                  {t("subscriptions.tenantManagement.history.periodStart")}
                </p>
                <p>
                  {formatDateTime(
                    isIncompleteExpired
                      ? null
                      : subscription.currentPeriodStart,
                  )}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs tracking-[0.12em] uppercase">
                  {t("subscriptions.tenantManagement.history.periodEnd")}
                </p>
                <p>
                  {formatDateTime(
                    isIncompleteExpired ? null : subscription.currentPeriodEnd,
                  )}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs tracking-[0.12em] uppercase">
                  {t(
                    "subscriptions.tenantManagement.history.cancelAtPeriodEnd",
                  )}
                </p>
                <p>
                  {subscription.cancelAtPeriodEnd
                    ? t("subscriptions.plans.yes")
                    : t("subscriptions.plans.no")}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs tracking-[0.12em] uppercase">
                  {t("subscriptions.tenantManagement.history.stripeId")}
                </p>
                <p className="font-mono text-xs break-all">
                  {subscription.stripeSubscriptionId ?? "—"}
                </p>
              </div>
            </div>

            <Separator className="my-4" />

            <div className="flex flex-col gap-2">
              <p className="text-muted-foreground text-xs tracking-[0.12em] uppercase">
                {t(
                  "subscriptions.tenantManagement.planDetails.includedFeatures",
                )}
              </p>
              <TenantPlanFeatureBadges
                features={normalizedFeatures}
                enableCoreFeatures={
                  !isIncompleteExpired && subscription.plan.enableCoreFeatures
                }
                planTitle={subscription.plan.title}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
